import { clashKey, type ClashResult } from '@viewer/clash/lib'
import {
  ESPECIALIDADES,
  PRIORITIES,
  triageOf,
  type ClashPriority,
  type ClashTriageMap,
  type ClashTriagePatch,
  type ClashTriageState,
} from './clashTriage'

// ---------------------------------------------------------------------------
// A lista "Por grupos": o terceiro passo do funil (docs/CLASH.md,
// seccao 11).
//
// Com 1191 conflitos a lista por conflito nao se gere; com 107 grupos
// sim. Cada linha e um grupo (o titulo, a prioridade, quantas
// interferencias, o estado agregado, o responsavel), as accoes em lote do
// grupo passam para todos os conflitos dele, e abrir o grupo mostra os
// seus conflitos.
//
// Filtros rapidos, sobre a lista de trabalho (sem os ignorados por
// regra): "So para reuniao" (Critica e Alta, pela prioridade do grupo ou
// do conflito sem grupo), especialidade responsavel (a primeira sigla do
// responsavel: "MAC (propoe) / EST (dimensiona)" e MAC) e nivel.
//
// Puro.
// ---------------------------------------------------------------------------

/** Prioridades "para reuniao". */
export const PRIORIDADES_DE_REUNIAO: readonly ClashPriority[] = ['critica', 'alta']

const ORDEM_ESTADO: ClashTriageState[] = ['reaberto', 'novo', 'aceite', 'resolvido']

const RE_SIGLA = new RegExp(`\\b(${ESPECIALIDADES.map((e) => e.sigla).join('|')})\\b`)

/** A especialidade responsavel: a primeira sigla do texto, ou o texto. */
export function especialidadeResponsavel(assignedTo: string | undefined | null): string | null {
  const t = (assignedTo ?? '').trim()
  if (!t) return null
  const m = RE_SIGLA.exec(t)
  return m ? m[1] : t
}

function maisAlta(ps: readonly (ClashPriority | undefined | null)[]): ClashPriority | null {
  let out: ClashPriority | null = null
  for (const p of ps) if (p && (out === null || PRIORITIES.indexOf(p) < PRIORITIES.indexOf(out))) out = p
  return out
}

export interface LinhaDeGrupo {
  id: string
  titulo: string
  /** A mais alta dos conflitos do grupo na lista. */
  prioridade: ClashPriority | null
  results: ClashResult[]
  chaves: string[]
  /** O menos avancado (um conflito por decidir deixa o grupo por
   *  decidir), e a contagem por estado. */
  estado: ClashTriageState
  estados: Partial<Record<ClashTriageState, number>>
  /** O responsavel comum, ou null se nenhum; "varios" quando diferem. */
  responsavel: string | null
  niveis: string[]
}

/** Prioridade de cada grupo (a mais alta dos seus conflitos na lista). */
export function prioridadesDosGrupos(
  results: readonly ClashResult[],
  triage: ClashTriageMap,
): Map<string, ClashPriority | null> {
  const ps = new Map<string, (ClashPriority | undefined)[]>()
  for (const r of results) {
    const e = triage[clashKey(r)]
    if (!e?.group) continue
    const l = ps.get(e.group)
    if (l) l.push(e.priority)
    else ps.set(e.group, [e.priority])
  }
  return new Map([...ps].map(([g, l]) => [g, maisAlta(l)]))
}

/** As linhas dos grupos e os conflitos sem grupo. Ordem: prioridade, e
 *  depois o titulo. */
export function linhasDosGrupos(
  results: readonly ClashResult[],
  triage: ClashTriageMap,
  grupos: Readonly<Record<string, { titulo?: string }>> = {},
): { linhas: LinhaDeGrupo[]; semGrupo: ClashResult[] } {
  const porId = new Map<string, ClashResult[]>()
  const semGrupo: ClashResult[] = []
  for (const r of results) {
    const g = triage[clashKey(r)]?.group
    if (!g) {
      semGrupo.push(r)
      continue
    }
    const l = porId.get(g)
    if (l) l.push(r)
    else porId.set(g, [r])
  }
  const linhas: LinhaDeGrupo[] = []
  for (const [id, rs] of porId) {
    const chaves = rs.map(clashKey)
    const entradas = chaves.map((k) => triageOf(triage, k))
    const estados: Partial<Record<ClashTriageState, number>> = {}
    let estado: ClashTriageState = 'resolvido'
    for (const e of entradas) {
      estados[e.state] = (estados[e.state] ?? 0) + 1
      if (ORDEM_ESTADO.indexOf(e.state) < ORDEM_ESTADO.indexOf(estado)) estado = e.state
    }
    const responsaveis = [...new Set(entradas.map((e) => (e.assignedTo ?? '').trim()).filter(Boolean))]
    const titulo = entradas.find((e) => e.groupTitle)?.groupTitle ?? grupos[id]?.titulo ?? id
    linhas.push({
      id,
      titulo,
      prioridade: maisAlta(entradas.map((e) => e.priority)),
      results: rs,
      chaves,
      estado,
      estados,
      responsavel: responsaveis.length === 0 ? null : responsaveis.length === 1 ? responsaveis[0] : 'vários',
      niveis: [...new Set(entradas.map((e) => e.nivel).filter((n): n is string => !!n))].sort(),
    })
  }
  linhas.sort(
    (a, b) =>
      (a.prioridade ? PRIORITIES.indexOf(a.prioridade) : 9) - (b.prioridade ? PRIORITIES.indexOf(b.prioridade) : 9) ||
      a.titulo.localeCompare(b.titulo),
  )
  return { linhas, semGrupo }
}

export interface FiltrosRapidos {
  /** So Critica e Alta. */
  soReuniao: boolean
  /** Sigla da especialidade responsavel ('' = todas). */
  especialidade: string
  /** Nivel ('' = todos). */
  nivel: string
}

export const SEM_FILTROS: FiltrosRapidos = { soReuniao: false, especialidade: '', nivel: '' }

export function temFiltros(f: FiltrosRapidos): boolean {
  return f.soReuniao || f.especialidade !== '' || f.nivel !== ''
}

/**
 * Os conflitos que passam os filtros. "Para reuniao" conta pela
 * prioridade do grupo (um conflito Media num grupo Critico vai a reuniao
 * com o grupo), ou pela do conflito quando nao tem grupo.
 */
export function filtrarConflitos(
  results: readonly ClashResult[],
  triage: ClashTriageMap,
  f: FiltrosRapidos,
): ClashResult[] {
  if (!temFiltros(f)) return [...results]
  const prioGrupo = f.soReuniao ? prioridadesDosGrupos(results, triage) : new Map()
  return results.filter((r) => {
    const e = triage[clashKey(r)]
    if (f.soReuniao) {
      const p = e?.group ? prioGrupo.get(e.group) : e?.priority
      if (!p || !PRIORIDADES_DE_REUNIAO.includes(p)) return false
    }
    if (f.especialidade && especialidadeResponsavel(e?.assignedTo) !== f.especialidade) return false
    if (f.nivel && (e?.nivel ?? '') !== f.nivel) return false
    return true
  })
}

/** As opcoes dos filtros: especialidades responsaveis e niveis que
 *  aparecem na lista. */
export function opcoesDosFiltros(
  results: readonly ClashResult[],
  triage: ClashTriageMap,
): { especialidades: string[]; niveis: string[] } {
  const esps = new Set<string>()
  const niveis = new Set<string>()
  for (const r of results) {
    const e = triage[clashKey(r)]
    const s = especialidadeResponsavel(e?.assignedTo)
    if (s) esps.add(s)
    if (e?.nivel) niveis.add(e.nivel)
  }
  const ordem = ESPECIALIDADES.map((e) => e.sigla)
  return {
    especialidades: [...esps].sort((a, b) => {
      const ia = ordem.indexOf(a)
      const ib = ordem.indexOf(b)
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib) || a.localeCompare(b)
    }),
    niveis: [...niveis].sort((a, b) => a.localeCompare(b, 'pt', { numeric: true })),
  }
}

/** As contagens do topo da lista. */
export interface ContagensDoTopo {
  grupos: number
  paraReuniao: number
  /** Conflitos sem grupo na lista. */
  semGrupo: number
  ignorados: number
}

export function contagensDoTopo(
  linhas: readonly LinhaDeGrupo[],
  semGrupo: number,
  ignorados: number,
): ContagensDoTopo {
  return {
    grupos: linhas.length,
    paraReuniao: linhas.filter((l) => l.prioridade && PRIORIDADES_DE_REUNIAO.includes(l.prioridade)).length,
    semGrupo,
    ignorados,
  }
}

/** "107 grupos · 27 para reunião · 381 ignorados por regra". */
export function textoDoTopo(c: ContagensDoTopo): string {
  const partes = [
    `${c.grupos} grupo${c.grupos === 1 ? '' : 's'}`,
    `${c.paraReuniao} para reunião`,
  ]
  if (c.semGrupo > 0) partes.push(`${c.semGrupo} sem grupo`)
  partes.push(`${c.ignorados} ignorado${c.ignorados === 1 ? '' : 's'} por regra`)
  return partes.join(' · ')
}

/** O que a accao em lote de um grupo escreve. Campo vazio nao mexe. */
export interface LoteDoGrupo {
  state?: ClashTriageState | ''
  priority?: ClashPriority | ''
  assignedTo?: string
  recommendation?: string
  note?: string
}

export function patchDoLote(l: LoteDoGrupo): ClashTriagePatch | null {
  const p: ClashTriagePatch = {}
  if (l.state) p.state = l.state
  if (l.priority) p.priority = l.priority
  if (l.assignedTo?.trim()) p.assignedTo = l.assignedTo.trim()
  if (l.recommendation?.trim()) p.recommendation = l.recommendation.trim()
  if (l.note?.trim()) p.note = l.note.trim()
  return Object.keys(p).length > 0 ? p : null
}
